import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import DOMPurify from "dompurify";
import { db } from "../../firebase";
import "../solutions/Solutions.css";

const formatBlogDate = (dateString = "") => {
  if (!dateString) return "";

  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";

  const day = date.getDate();

  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";

  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${month} ${day}${suffix}, ${year}`;
};

const sanitizeBlogHtml = (html = "") =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "b", "em", "i", "u",
      "h1", "h2", "h3", "h4",
      "ul", "ol", "li",
      "blockquote",
      "a",
      "table", "thead", "tbody", "tr", "th", "td",
      "img"
    ],
    ALLOWED_ATTR: [
      "href", "target", "rel", "colspan", "rowspan",
      "src", "alt", "style"
    ],
  });

function BlogPost() {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);
  const [loadingBlog, setLoadingBlog] = useState(true);

  useEffect(() => {
    const loadBlog = async () => {
      setLoadingBlog(true);

      try {
        const q = query(
          collection(db, "documentMetadata"),
          where("type", "==", "blog"),
          where("slug", "==", slug)
        );

        const snap = await getDocs(q);

        if (snap.empty) {
          setBlog(null);
          return;
        }

        const docSnap = snap.docs[0];

        setBlog({
          id: docSnap.id,
          ...docSnap.data(),
        });
      } catch (err) {
        console.error("Error loading blog:", err);
        setBlog(null);
      } finally {
        setLoadingBlog(false);
      }
    };

    loadBlog();
  }, [slug]);

  useEffect(() => {
  if (!blog) return;

  const title =
    blog.seoTitle ||
    `${blog.title} | Go!Foton`;

  const description =
    blog.metaDescription ||
    blog.excerpt ||
    "";

  const keywords = [
    blog.seoKeywords,
    blog.aiKeywords,
  ]
    .filter(Boolean)
    .join(", ");

  const canonicalUrl =
    blog.canonicalUrl ||
    `https://gofoton.com/blog/${blog.slug}`;

  document.title = title;

  const setMeta = (
    selector,
    attribute,
    value
  ) => {
    if (!value) return;

    let element =
      document.head.querySelector(selector);

    if (!element) {
      element =
        document.createElement("meta");

      if (
        selector.includes("property=")
      ) {
        element.setAttribute(
          "property",
          attribute
        );
      } else {
        element.setAttribute(
          "name",
          attribute
        );
      }

      document.head.appendChild(element);
    }

    element.setAttribute(
      "content",
      value
    );
  };

  setMeta(
    'meta[name="description"]',
    "description",
    description
  );

  setMeta(
    'meta[name="keywords"]',
    "keywords",
    keywords
  );

  setMeta(
    'meta[property="og:title"]',
    "og:title",
    blog.socialTitle || title
  );

  setMeta(
    'meta[property="og:description"]',
    "og:description",
    blog.socialDescription ||
      description
  );

  setMeta(
    'meta[property="og:image"]',
    "og:image",
    blog.socialImage ||
      blog.image ||
      ""
  );

  setMeta(
    'meta[property="og:url"]',
    "og:url",
    canonicalUrl
  );

  let canonical =
    document.head.querySelector(
      'link[rel="canonical"]'
    );

  if (!canonical) {
    canonical =
      document.createElement("link");

    canonical.setAttribute(
      "rel",
      "canonical"
    );

    document.head.appendChild(
      canonical
    );
  }

  canonical.setAttribute(
    "href",
    canonicalUrl
  );
}, [blog]);

  if (loadingBlog) {
    return (
      <div className="contact-page">
        <div className="curved-header-sol2" style={{ paddingBottom: 60 }}>
          <div className="datacenter-left" style={{ paddingTop: 100 }}>
            <h1>Loading blog...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="contact-page">
        <div className="curved-header-sol2" style={{ paddingBottom: 60 }}>
          <div className="datacenter-left" style={{ paddingTop: 100 }}>
            <h1>Blog Not Found</h1>
            <p style={{ marginBottom: 40, fontSize: 20 }}>
              This blog post could not be found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const cleanHtml = sanitizeBlogHtml(blog.bodyHtml || "");

  return (
    <div className="contact-page">
      <div className="curved-header-sol2" style={{ paddingBottom: 60 }}>
        <div className="datacenter-left" style={{ paddingTop: 100 }}>
          <h1>{blog.title}</h1>

<div className="dynamic-blog-meta">
  <div className="dynamic-blog-byline">
    {blog.author && (
      <span>
        By <strong>{blog.author}</strong>
      </span>
    )}

    {blog.authorTitle && (
      <span>
        {blog.authorTitle}
      </span>
    )}

    {blog.date && (
      <span>
        {formatBlogDate(blog.date)}
      </span>
    )}
  </div>

  {Array.isArray(blog.relatedLinks) &&
    blog.relatedLinks.length > 0 && (
      <div className="dynamic-blog-related">
        <strong>Related Information:</strong>

        <div className="dynamic-blog-related-links">
          {blog.relatedLinks.map(
            (item, index) => (
              <a
                key={`${item.url}-${index}`}
                href={item.url}
                target={
                  /^https?:\/\//i.test(item.url)
                    ? "_blank"
                    : undefined
                }
                rel={
                  /^https?:\/\//i.test(item.url)
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {item.label || item.url}
              </a>
            )
          )}
        </div>
      </div>
    )}
</div>
        </div>

        <div className="datacenter-right" style={{ paddingTop: 60 }}>
          {blog.image && (
            <img
              src={blog.image}
              alt={blog.title}
              className="datacenter-img"
            />
          )}
        </div>
      </div>

      <div className="blog-section-1 dynamic-blog-section" style={{ marginTop: 30 }}>
        <div
          className="solution-section-1-description dynamic-blog-body"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      </div>

      <div className="curved-footer-solutions">
        <div
          className="curved-top-overlay-solutions"
          style={{ backgroundColor: "white" }}
        />

        <h2>Would you like to hear more?</h2>
        <p>Our experts would love to hear about your systems and how we can help</p>
        <a className="connect-button" href="/support">
          LET’S CONNECT
        </a>
      </div>
    </div>
  );
}

export default BlogPost;